import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState, useEffect } from "react";
import { Alert, Button, Loading } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useCreateSetupIntentMutation } from "@/redux/apis/subscription/subscriptionPlansApi";
import { addToast } from "@/utils/addToast";

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_RIDEMINDER_STRIPE_PUBLISHABLE_KEY || "");

// Payment Element appearance options
const PAYMENT_ELEMENT_OPTIONS = {
  layout: "tabs" as const,
};

interface PaymentFormInnerProps {
  onCardAdded: (paymentMethodId: string) => void;
  onCancel: () => void;
}

// Inner Payment Form Component (rendered inside Elements with clientSecret)
function PaymentFormInner({ onCardAdded, onCancel }: PaymentFormInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const handleSaveCard = async () => {
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      // Confirm the SetupIntent with PaymentElement
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      // Show success toast and redirect to card list
      if (result.setupIntent && result.setupIntent.payment_method) {
        addToast("success", "Payment method saved successfully!");
        onCardAdded(result.setupIntent.payment_method as string);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      addToast("danger", errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10">
          <Loading className="rounded-lg" />
        </div>
      )}
      <div className={loading ? "invisible" : ""}>
        <div className="space-y-4">
          <div className="border-neutral-light-gray rounded-lg border bg-white p-4">
            <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} onReady={() => setPaymentElementReady(true)} />
          </div>

          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="md"
              onClick={handleSaveCard}
              disabled={!stripe || !paymentElementReady || loading}
              className="w-full"
            >
              Save Payment Method
            </Button>
            <Button variant="secondary" size="md" onClick={onCancel} disabled={loading} className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AddCardFormProps {
  providerId: string;
  onCardAdded: (paymentMethodId: string) => void;
  onCancel: () => void;
}

// Add New Card Form Component
export const AddCardForm = ({ providerId, onCardAdded, onCancel }: AddCardFormProps) => {
  const [createSetupIntent, { isLoading }] = useCreateSetupIntentMutation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch SetupIntent when component mounts
  useEffect(() => {
    const fetchSetupIntent = async () => {
      try {
        const setupIntent = await createSetupIntent({ providerId }).unwrap();
        setClientSecret(setupIntent.client_secret);
      } catch (err) {
        const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
        const message = errors[0] || "Failed to create setup intent";
        setError(
          message.toLowerCase().includes("payment configuration not found")
            ? "payment_config_not_found"
            : message
        );
      }
    };

    fetchSetupIntent();
  }, [providerId, createSetupIntent]);

  if (isLoading) {
    return (
      <div className="relative min-h-[200px]">
        <Loading className="rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert
          type="danger"
          message={
            error === "payment_config_not_found" ? (
              <>Please complete your payment configuration in the <span className="text-neutral-dark-gray">Payment Configuration</span> tab (Stripe, Deposits, and Debits) before adding a payment method.</>
            ) : (
              error
            )
          }
        />
        <Button variant="secondary" size="lg" onClick={onCancel} className="w-full">
          Cancel
        </Button>
      </div>
    );
  }

  if (!clientSecret) {
    return <Alert type="warning" message="Unable to initialize payment form" />;
  }

  // Render Elements with the clientSecret for PaymentElement
  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: "#570df8",
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentFormInner onCardAdded={onCardAdded} onCancel={onCancel} />
    </Elements>
  );
};
